// Amadeus branded-fare upsell + fare choice.
//
// flightOffersUpsell (Amadeus `shopping/flight-offers/upselling`) reprices
// the selected offer against every branded fare available for that
// routing and returns them as raw flight-offers. This module owns the
// entire fare-choice pipeline:
//
//   fetch_upsell         — POST flightOffersUpsell, normalize results
//   get_branded_fare     — extract the brand identifier (Amadeus uses the
//                          brandedFare string on fareDetailsBySegment[0])
//   choose_upsell        — the "pick" action; returns the offer that
//                          should be committed to the store. Pure pass-
//                          through for Amadeus — the watcher on
//                          flight_offer in detail.vue triggers the reprice
//                          naturally. Providers that need a round-trip on
//                          select can override the corresponding class
//                          method.
//
// No raw envelope leaves this module — every output is a normalized
// FlightOffer that carries _raw for the next reprice / book step.

import { service_handler } from '@/services';
import { normalize, extract_offers_array } from './normalize';
import { merge_dictionaries } from '../base/helpers';
import { extract_dictionaries } from './dictionaries';
import { log } from '@/utilities/commons';

/**
 * POST flightOffersUpsell, normalize the returned branded-fare variants.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer
 * @returns {Promise<Object[]>}
 */
export const fetch_upsell = (provider, flight_offer) => {
  const body = {
    priceFlightOffersBody: {
      data: {
        type: 'flight-offers-pricing',
        flightOffers: [flight_offer._raw],
      },
    },
  };
  return service_handler('flightOffersUpsell', body).then((response) => {
    merge_dictionaries(provider, extract_dictionaries(response));
    // Per-item try/catch — one malformed upsell shouldn't reject the
    // whole branded-fare list. Drop bad entries with a log.
    const upsells = [];
    extract_offers_array(response).forEach((raw_offer) => {
      try {
        upsells.push(normalize(provider, raw_offer));
      } catch (e) {
        log('amadeus.fetch_upsell: skipping malformed upsell', e);
      }
    });
    return upsells;
  });
};

/**
 * Amadeus keys every branded-fare variant by the `brandedFare` string on
 * the first fare-detail. An offer with no brandedFare (vanilla GDS)
 * returns ''; callers should treat that as "ungrouped".
 *
 * @param {Object} flight_offer
 * @returns {string}
 */
export const get_branded_fare = (flight_offer) => {
  const { travelerPricings = [] } = flight_offer || {};
  const { fareDetailsBySegment = [] } = travelerPricings[0] || {};
  const { brandedFare = '' } = fareDetailsBySegment[0] || {};
  return brandedFare;
};

/**
 * The "pick" action. Amadeus doesn't need an upstream round-trip to
 * confirm the choice — detail.vue's watcher on flight_offer fires
 * get_offer_details which repays that role. Returning the offer as-is
 * keeps the class method shape consistent across providers.
 *
 * @param {Provider} _provider
 * @param {Object} flight_offer
 * @returns {Promise<Object|null>}
 */
export const choose_upsell = (_provider, flight_offer) =>
  Promise.resolve(flight_offer || null);
